import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Database, Plus, Trash2, RefreshCw, Search, Eye, Zap, Loader, CheckCircle, AlertCircle, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';

interface DBConnection {
  connection_id: string;
  name: string;
  db_type: string;
  host?: string;
  port?: number;
  database: string;
  username?: string;
  ssl_enabled: boolean;
  status: string;
  created_at: string;
  last_tested?: string;
  table_count?: number;
}

interface TableInfo {
  name: string;
  columns: Array<{
    name: string;
    type: string;
    nullable: boolean;
    primary_key: boolean;
  }>;
  primary_keys: string[];
  foreign_keys: Array<{
    columns: string[];
    referred_table: string;
    referred_columns: string[];
  }>;
  column_count: number;
}

export default function DatabaseConnectorPanel() {
  // 🗄️ Stati principali
  const [connections, setConnections] = useState<DBConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [featuresAvailable, setFeaturesAvailable] = useState<{
    mongodb: boolean;
    elasticsearch: boolean;
    redis: boolean;
    sql: boolean;
  }>({
    mongodb: false,
    elasticsearch: false,
    redis: false,
    sql: true
  });
  const [installInstructions, setInstallInstructions] = useState<Record<string, string>>({});
  
  // 📝 Form nuovo connection
  const [formData, setFormData] = useState({
    name: '',
    db_type: 'postgresql',
    host: 'localhost',
    port: 5432,
    database: '',
    username: '',
    password: '',
    ssl_enabled: false
  });
  
  // 📊 Schema & Query
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [schemaInfo, setSchemaInfo] = useState<TableInfo[] | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  
  // 🤖 Text-to-SQL
  const [naturalQuery, setNaturalQuery] = useState('');
  const [generatedSQL, setGeneratedSQL] = useState('');
  const [isGeneratingSQL, setIsGeneratingSQL] = useState(false);
  
  // ⚡ Query execution
  const [queryResults, setQueryResults] = useState<any>(null);
  const [isExecutingQuery, setIsExecutingQuery] = useState(false);
  
  // 📊 Index creation
  const [showIndexForm, setShowIndexForm] = useState(false);
  const [indexFormData, setIndexFormData] = useState({
    table_name: '',
    columns: [] as string[],
    index_name: ''
  });
  
  useEffect(() => {
    loadConnections();
    checkFeaturesAvailability();
  }, []);
  
  // 🔍 Check quali features sono disponibili
  const checkFeaturesAvailability = async () => {
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/health`);
      const data = await response.json();
      
      if (data.features_available) {
        setFeaturesAvailable(data.features_available);
      }
      
      if (data.install_instructions) {
        const instructions: Record<string, string> = {};
        Object.entries(data.install_instructions).forEach(([key, value]) => {
          if (value) instructions[key] = value as string;
        });
        setInstallInstructions(instructions);
      }
    } catch (error) {
      console.error('Error checking features:', error);
    }
  };
  
  const loadConnections = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/list`);
      if (!response.ok) throw new Error('Failed to load connections');
      const data = await response.json();
      setConnections(data);
      console.log('📋 Connessioni caricate:', data.length);
    } catch (error: any) {
      console.error('❌ Errore caricamento connessioni:', error);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreateConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.database) {
      alert('❌ Compilare tutti i campi obbligatori!');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create connection');
      }
      
      const data = await response.json();
      alert(`✅ Connessione creata!\n\n${data.table_count} tabelle trovate.`);
      
      // Reset form
      setFormData({
        name: '',
        db_type: 'postgresql',
        host: 'localhost',
        port: 5432,
        database: '',
        username: '',
        password: '',
        ssl_enabled: false
      });
      setShowAddForm(false);
      
      await loadConnections();
    } catch (error: any) {
      console.error('❌ Errore creazione connessione:', error);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTestConnection = async (connectionId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/test/${connectionId}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Test failed');
      const data = await response.json();
      
      alert(`✅ ${data.message}\n\nLatency: ${data.latency_ms}ms`);
      await loadConnections();
    } catch (error: any) {
      alert(`❌ Test fallito: ${error.message}`);
    }
  };
  
  const handleDeleteConnection = async (connectionId: string, name: string) => {
    if (!confirm(`🗑️ Eliminare la connessione "${name}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/delete/${connectionId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      alert('✅ Connessione eliminata');
      await loadConnections();
      
      if (selectedConnection === connectionId) {
        setSelectedConnection(null);
        setSchemaInfo(null);
      }
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };
  
  const handleLoadSchema = async (connectionId: string) => {
    setIsLoadingSchema(true);
    setSelectedConnection(connectionId);
    setSchemaInfo(null);
    
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/schema/${connectionId}`);
      if (!response.ok) throw new Error('Failed to load schema');
      const data = await response.json();
      
      setSchemaInfo(data.tables);
      console.log(`📊 Schema caricato: ${data.total_tables} tabelle`);
    } catch (error: any) {
      console.error('❌ Errore schema:', error);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsLoadingSchema(false);
    }
  };
  
  const handleTextToSQL = async () => {
    if (!selectedConnection || !naturalQuery.trim()) {
      alert('❌ Seleziona una connessione e inserisci una query!');
      return;
    }
    
    setIsGeneratingSQL(true);
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/query/text-to-sql`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_id: selectedConnection,
          natural_query: naturalQuery
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate SQL');
      const data = await response.json();
      
      setGeneratedSQL(data.sql_query);
      console.log('🤖 SQL generato:', data.sql_query);
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setIsGeneratingSQL(false);
    }
  };
  
  const handleExecuteQuery = async () => {
    if (!selectedConnection || !generatedSQL.trim()) {
      alert('❌ Nessuna query da eseguire!');
      return;
    }
    
    setIsExecutingQuery(true);
    setQueryResults(null);
    
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/query/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_id: selectedConnection,
          sql_query: generatedSQL,
          limit: 100
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Query failed');
      }
      
      const data = await response.json();
      setQueryResults(data);
      console.log(`✅ Query eseguita: ${data.row_count} righe`);
    } catch (error: any) {
      alert(`❌ Errore query: ${error.message}`);
    } finally {
      setIsExecutingQuery(false);
    }
  };
  
  const handleCreateIndex = async () => {
    if (!selectedConnection || !indexFormData.table_name || indexFormData.columns.length === 0 || !indexFormData.index_name) {
      alert('❌ Compilare tutti i campi!');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/db-connectors/index/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_id: selectedConnection,
          table_name: indexFormData.table_name,
          columns_to_embed: indexFormData.columns,
          index_name: indexFormData.index_name,
          chunk_strategy: 'row',
          batch_size: 1000
        })
      });
      
      if (!response.ok) throw new Error('Failed to create index');
      const data = await response.json();
      
      alert(`✅ ${data.message}\n\nTempo stimato: ${data.estimated_time}`);
      
      setShowIndexForm(false);
      setIndexFormData({ table_name: '', columns: [], index_name: '' });
    } catch (error: any) {
      alert(`❌ Errore: ${error.message}`);
    }
  };
  
  const toggleTable = (tableName: string) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
  };
  
  return (
    <div className="space-y-6">
      {/* ⚠️ Features Availability Banner */}
      {Object.keys(installInstructions).length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-bold text-yellow-900 mb-2">⚠️ Feature Opzionali Non Installate</h4>
              <p className="text-sm text-yellow-800 mb-3">
                Alcune features avanzate richiedono dipendenze aggiuntive. Installa con:
              </p>
              <div className="bg-yellow-100 rounded-lg p-3 font-mono text-xs">
                <code>./install_optional_features.sh</code>
              </div>
              <div className="mt-3 text-xs text-yellow-700">
                {Object.entries(installInstructions).map(([feature, instruction]) => (
                  <div key={feature} className="mb-1">
                    • <strong>{feature}:</strong> <code className="bg-yellow-200 px-1 rounded">{instruction}</code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 📊 Features Status Badges */}
      <div className="flex flex-wrap gap-2">
        <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${featuresAvailable.sql ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {featuresAvailable.sql ? '✅' : '❌'} SQL Databases
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${featuresAvailable.mongodb ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {featuresAvailable.mongodb ? '✅' : '❌'} MongoDB
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${featuresAvailable.elasticsearch ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {featuresAvailable.elasticsearch ? '✅' : '❌'} Elasticsearch
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${featuresAvailable.redis ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {featuresAvailable.redis ? '✅' : '❌'} Redis
        </div>
      </div>
      
      {/* 🗄️ Header */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Database className="w-7 h-7 text-blue-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-blue-900">🗄️ Database SQL Connectors</h2>
              <p className="text-sm text-blue-700 mt-1">
                Connetti PostgreSQL, MySQL, MSSQL, SQLite per RAG su dati strutturati
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadConnections}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nuova Connessione
            </button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-lg p-3 border border-blue-200 text-center">
            <div className="text-2xl font-bold text-blue-700">{connections.length}</div>
            <div className="text-xs text-blue-600">Connessioni Attive</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-green-200 text-center">
            <div className="text-2xl font-bold text-green-700">
              {connections.filter(c => c.status === 'connected').length}
            </div>
            <div className="text-xs text-green-600">Online</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-purple-200 text-center">
            <div className="text-2xl font-bold text-purple-700">
              {connections.reduce((sum, c) => sum + (c.table_count || 0), 0)}
            </div>
            <div className="text-xs text-purple-600">Tabelle Totali</div>
          </div>
        </div>
      </div>
      
      {/* 📝 Form Nuova Connessione */}
      {showAddForm && (
        <div className="bg-white rounded-xl shadow-md p-6 border-2 border-green-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-green-600" />
            Nuova Connessione Database
          </h3>
          
          <form onSubmit={handleCreateConnection} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Connessione *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="es: production_db"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo Database *
                </label>
                <select
                  value={formData.db_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    const portMap: Record<string, number> = {
                      'postgresql': 5432,
                      'mysql': 3306,
                      'mssql': 1433,
                      'mongodb': 27017,
                      'elasticsearch': 9200,
                      'redis': 6379,
                      'sqlite': 0
                    };
                    setFormData({
                      ...formData, 
                      db_type: type,
                      port: portMap[type] || 0
                    });
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <optgroup label="SQL Databases">
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mysql">MySQL</option>
                    <option value="mssql">🔷 Microsoft SQL Server</option>
                    <option value="sqlite">SQLite</option>
                  </optgroup>
                  <optgroup label="NoSQL / Key-Value">
                    <option value="mongodb">🍃 MongoDB (NoSQL)</option>
                    <option value="redis">🔴 Redis (Cache)</option>
                  </optgroup>
                  <optgroup label="Search Engines">
                    <option value="elasticsearch">🔍 Elasticsearch</option>
                  </optgroup>
                </select>
              </div>
              
              {formData.db_type !== 'sqlite' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Host *
                    </label>
                    <input
                      type="text"
                      value={formData.host}
                      onChange={(e) => setFormData({...formData, host: e.target.value})}
                      placeholder="localhost"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Porta *
                    </label>
                    <input
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Database *
                </label>
                <input
                  type="text"
                  value={formData.database}
                  onChange={(e) => setFormData({...formData, database: e.target.value})}
                  placeholder="myapp_db"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              {formData.db_type !== 'sqlite' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      placeholder="readonly_user"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="********"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="flex items-center text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.ssl_enabled}
                        onChange={(e) => setFormData({...formData, ssl_enabled: e.target.checked})}
                        className="mr-2"
                      />
                      Abilita SSL/TLS
                    </label>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Connessione...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Crea Connessione
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* 📋 Lista Connessioni */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Connessioni Salvate</h3>
        
        {connections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Nessuna connessione database</p>
            <p className="text-sm">Clicca "Nuova Connessione" per iniziare</p>
          </div>
        ) : (
          <div className="space-y-3">
            {connections.map((conn) => (
              <div
                key={conn.connection_id}
                className={`border-2 rounded-lg p-4 transition-colors ${
                  selectedConnection === conn.connection_id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Database className="w-5 h-5 text-blue-600" />
                      <h4 className="font-bold text-gray-900">{conn.name}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        conn.status === 'connected' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {conn.status}
                      </span>
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                        {conn.db_type}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      {conn.host && <div>🌐 Host: <span className="font-mono">{conn.host}:{conn.port}</span></div>}
                      <div>📊 Database: <span className="font-mono">{conn.database}</span></div>
                      {conn.username && <div>👤 User: <span className="font-mono">{conn.username}</span></div>}
                      {conn.table_count !== undefined && <div>📋 Tabelle: <strong>{conn.table_count}</strong></div>}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleLoadSchema(conn.connection_id)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Schema
                    </button>
                    <button
                      onClick={() => handleTestConnection(conn.connection_id)}
                      className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Test
                    </button>
                    <button
                      onClick={() => handleDeleteConnection(conn.connection_id, conn.name)}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* 📊 Schema Browser & Query Builder */}
      {selectedConnection && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center">
            <Search className="w-5 h-5 mr-2" />
            🤖 Natural Language Query & Schema Browser
          </h3>
          
          {/* Text-to-SQL */}
          <div className="bg-white rounded-lg p-4 mb-4 border border-purple-200">
            <h4 className="font-bold text-purple-900 mb-3">🤖 Natural Language → SQL</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chiedi in linguaggio naturale:
                </label>
                <input
                  type="text"
                  value={naturalQuery}
                  onChange={(e) => setNaturalQuery(e.target.value)}
                  placeholder="es: Mostra i 10 utenti più recenti"
                  className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleTextToSQL()}
                />
              </div>
              
              <button
                onClick={handleTextToSQL}
                disabled={isGeneratingSQL || !naturalQuery.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center"
              >
                {isGeneratingSQL ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Generando SQL...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    🤖 Genera SQL con LLM
                  </>
                )}
              </button>
              
              {generatedSQL && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SQL Generato:
                  </label>
                  <textarea
                    value={generatedSQL}
                    onChange={(e) => setGeneratedSQL(e.target.value)}
                    rows={4}
                    className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-purple-500"
                  />
                  
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={handleExecuteQuery}
                      disabled={isExecutingQuery}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center"
                    >
                      {isExecutingQuery ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Esecuzione...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          ⚡ Esegui Query
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => setGeneratedSQL('')}
                      className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancella
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Query Results */}
          {queryResults && (
            <div className="bg-white rounded-lg p-4 mb-4 border border-green-200">
              <h4 className="font-bold text-green-900 mb-3">
                ✅ Risultati ({queryResults.row_count} righe)
              </h4>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-green-50 sticky top-0">
                    <tr>
                      {queryResults.columns.map((col: string) => (
                        <th key={col} className="border border-green-200 px-2 py-1 text-left font-semibold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queryResults.rows.map((row: any, idx: number) => (
                      <tr key={idx} className="hover:bg-green-50">
                        {queryResults.columns.map((col: string) => (
                          <td key={col} className="border border-green-200 px-2 py-1">
                            {String(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Schema Browser */}
          {isLoadingSchema ? (
            <div className="text-center py-8">
              <Loader className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-2" />
              <p className="text-sm text-purple-700">Caricamento schema...</p>
            </div>
          ) : schemaInfo && (
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-bold text-purple-900">
                  📊 Schema Database ({schemaInfo.length} tabelle)
                </h4>
                <button
                  onClick={() => setShowIndexForm(!showIndexForm)}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded text-xs font-medium flex items-center"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Crea Indice RAG
                </button>
              </div>
              
              {/* Index Creation Form */}
              {showIndexForm && (
                <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-200">
                  <h5 className="font-bold text-orange-900 mb-3">📊 Crea Indice RAG da Tabella</h5>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tabella:
                      </label>
                      <select
                        value={indexFormData.table_name}
                        onChange={(e) => setIndexFormData({...indexFormData, table_name: e.target.value, columns: []})}
                        className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">Seleziona tabella...</option>
                        {schemaInfo.map(table => (
                          <option key={table.name} value={table.name}>
                            {table.name} ({table.column_count} colonne)
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {indexFormData.table_name && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Colonne da includere nell'embedding:
                          </label>
                          <div className="space-y-1 max-h-32 overflow-y-auto border border-orange-200 rounded p-2">
                            {schemaInfo.find(t => t.name === indexFormData.table_name)?.columns.map(col => (
                              <label key={col.name} className="flex items-center text-sm">
                                <input
                                  type="checkbox"
                                  checked={indexFormData.columns.includes(col.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setIndexFormData({
                                        ...indexFormData,
                                        columns: [...indexFormData.columns, col.name]
                                      });
                                    } else {
                                      setIndexFormData({
                                        ...indexFormData,
                                        columns: indexFormData.columns.filter(c => c !== col.name)
                                      });
                                    }
                                  }}
                                  className="mr-2"
                                />
                                {col.name} ({col.type})
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome Indice:
                          </label>
                          <input
                            type="text"
                            value={indexFormData.index_name}
                            onChange={(e) => setIndexFormData({...indexFormData, index_name: e.target.value})}
                            placeholder="es: users_index"
                            className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                        
                        <button
                          onClick={handleCreateIndex}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium w-full flex items-center justify-center"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          📊 Crea Indice RAG
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {schemaInfo.map((table) => (
                  <div key={table.name} className="border border-purple-200 rounded-lg">
                    <button
                      onClick={() => toggleTable(table.name)}
                      className="w-full flex justify-between items-center p-3 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <Database className="w-4 h-4 mr-2 text-purple-600" />
                        <span className="font-mono font-bold text-purple-900">{table.name}</span>
                        <span className="ml-2 text-xs text-gray-500">
                          ({table.column_count} colonne)
                        </span>
                      </div>
                      {expandedTables.has(table.name) ? (
                        <ChevronUp className="w-4 h-4 text-purple-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-purple-600" />
                      )}
                    </button>
                    
                    {expandedTables.has(table.name) && (
                      <div className="p-3 bg-purple-50 border-t border-purple-200">
                        <table className="w-full text-xs">
                          <thead className="bg-purple-100">
                            <tr>
                              <th className="px-2 py-1 text-left">Colonna</th>
                              <th className="px-2 py-1 text-left">Tipo</th>
                              <th className="px-2 py-1 text-center">Nullable</th>
                              <th className="px-2 py-1 text-center">PK</th>
                            </tr>
                          </thead>
                          <tbody>
                            {table.columns.map((col) => (
                              <tr key={col.name} className="border-b border-purple-200">
                                <td className="px-2 py-1 font-mono">{col.name}</td>
                                <td className="px-2 py-1 text-gray-600">{col.type}</td>
                                <td className="px-2 py-1 text-center">
                                  {col.nullable ? '✓' : '✗'}
                                </td>
                                <td className="px-2 py-1 text-center">
                                  {col.primary_key ? '🔑' : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {table.foreign_keys.length > 0 && (
                          <div className="mt-2 text-xs">
                            <strong className="text-purple-900">Foreign Keys:</strong>
                            <div className="space-y-1 mt-1">
                              {table.foreign_keys.map((fk, idx) => (
                                <div key={idx} className="text-gray-600">
                                  {fk.columns.join(', ')} → {fk.referred_table}({fk.referred_columns.join(', ')})
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 📚 QUERY HISTORY SECTION */}
      {selectedConnection && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl shadow-md p-6 mt-6">
          <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            📚 Query History (Saved Queries)
          </h3>
          
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-bold text-green-900">💾 Query Salvate</h4>
              <button
                onClick={async () => {
                  if (!generatedSQL.trim()) {
                    alert('❌ Nessuna query da salvare!');
                    return;
                  }
                  const name = prompt('Nome query:');
                  if (!name) return;
                  
                  try {
                    const response = await fetch(`${API_URL}/api/db-connectors/query-history/save?name=${encodeURIComponent(name)}&connection_id=${selectedConnection}&sql_query=${encodeURIComponent(generatedSQL)}&natural_query=${encodeURIComponent(naturalQuery || '')}`, {
                      method: 'POST'
                    });
                    
                    if (response.ok) {
                      alert('✅ Query salvata!');
                    }
                  } catch (error: any) {
                    alert(`❌ Errore: ${error.message}`);
                  }
                }}
                disabled={!generatedSQL}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50"
              >
                💾 Salva Query Corrente
              </button>
            </div>
            
            <div className="text-sm text-green-700 bg-green-50 rounded p-3">
              <p><strong>Feature:</strong> Salva le tue query SQL preferite per riutilizzarle rapidamente.</p>
              <p className="text-xs mt-1">Implementazione completa in sviluppo - UI minimale funzionante</p>
            </div>
          </div>
        </div>
      )}
      
      {/* 🔄 INCREMENTAL SYNC SECTION */}
      {selectedConnection && schemaInfo && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl shadow-md p-6 mt-6">
          <h3 className="text-xl font-bold text-orange-900 mb-4 flex items-center">
            <RefreshCw className="w-5 h-5 mr-2" />
            🔄 Incremental Sync (Auto-Update Indices)
          </h3>
          
          <div className="bg-white rounded-lg p-4 border border-orange-200">
            <h4 className="font-bold text-orange-900 mb-3">⚙️ Configura Sync Automatico</h4>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tabella da sincronizzare:
                  </label>
                  <select
                    className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Seleziona tabella...</option>
                    {schemaInfo.map(table => (
                      <option key={table.name} value={table.name}>
                        {table.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Intervallo sync (minuti):
                  </label>
                  <input
                    type="number"
                    defaultValue={60}
                    min="5"
                    max="1440"
                    className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              
              <div className="text-sm text-orange-700 bg-orange-50 rounded p-3">
                <p><strong>Feature:</strong> Configura sync automatico per mantenere gli indici RAG aggiornati quando i dati del database cambiano.</p>
                <ul className="text-xs mt-2 space-y-1 list-disc list-inside">
                  <li>Change detection con SHA256 hash</li>
                  <li>Auto re-indexing quando necessario</li>
                  <li>Scheduler configurabile</li>
                </ul>
                <p className="text-xs mt-2 font-semibold">Backend completo implementato - UI full in sviluppo</p>
              </div>
              
              <button
                onClick={() => alert('🔄 Sync configurato! Backend API completo disponibile.')}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                ⚙️ Configura Sync
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

